"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Geeft `[ref, nabij]`: `nabij` wordt true (en blijft true) zodra het element waar `ref` aan hangt
 * bijna in beeld komt (standaard 600 px eronder). Voor blokken onderaan een pagina die pas data uit
 * Firestore nodig hebben als de bezoeker erheen scrolt (MOW-PERF-OKT26): zo komt de Firestore-SDK
 * niet mee in het laden van een productpagina die niemand tot onderaan leest.
 *
 * Zonder IntersectionObserver (zeer oude browsers) is `nabij` meteen true, zoals voor de wijziging.
 * Het element moet er bij het eerste renderen al zijn (een plaatshouder van 1 px volstaat).
 */
export function useBijnaInBeeld<T extends Element = HTMLDivElement>(marge = "600px") {
  const ref = useRef<T | null>(null);
  const [nabij, setNabij] = useState(false);

  useEffect(() => {
    if (nabij) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      const timer = window.setTimeout(() => setNabij(true), 0);
      return () => window.clearTimeout(timer);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNabij(true);
          io.disconnect();
        }
      },
      { rootMargin: marge }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [nabij, marge]);

  return [ref, nabij] as const;
}
